import type { ICommonResponse } from "services/common-types";

import { authApi } from "../auth";
import { getUrlFromPA } from "../utils";

import type {
  IClientsFundsConsolidated,
  IFundDetails,
  IMutualFund,
  IPurchaseRequest,
  IOrderDocumentsWrapper,
  IOrderDocumentsRequest,
  ICreateDocumentsResponse,
  ISignDocumentsRequest,
  ISignDocumentsResponse,
} from "./types";

const containsRussianLetters = (str: string): boolean => {
  const russianLettersRegex = /[ЁА-яё]/;
  return russianLettersRegex.test(str);
};

export const mutualFundsApi = authApi.injectEndpoints({
  endpoints: (build) => ({
    /**
     * Информация о фондах и их оценки на заданную дату
     */
    clientsMutualFunds: build.query<
      IMutualFund[],
      {
        onDate: string;
      }
    >({
      query: ({ onDate }) => ({
        url: getUrlFromPA(`PA/MF/ClientsMutualFunds/${onDate}`),
        method: "GET",
      }),
    }),
    /**
     * Детализация операций по фонду
     */
    fundDetails: build.query<
      IFundDetails,
      {
        fundId: number;
        onDate: string;
      }
    >({
      query: ({ fundId, onDate }) => ({
        url: getUrlFromPA(`PA/MF/FundDetails/${fundId}/${onDate}`),
        method: "GET",
      }),
    }),
    /**
     * Заявки на приобретение
     */
    purchaseRequests: build.query<IPurchaseRequest[], void>({
      query: () => ({
        url: getUrlFromPA(`PA/MF/PurchaseRequests`),
        method: "GET",
      }),

      // todo Хак, сделан по просьбе бизнеса
      // todo Не выводим заявки, в которых поле Account(номер лицевого счета) не является числом
      //transformResponse: (response: IPurchaseRequest[]) =>
      //response.filter((item) => !isNaN(Number(item.Account))),
      // 02.10.2023 внесены изменения по задаче DGTST-8114 - выводим все заявки, там есть пустые номера счетов
      /*
      transformResponse: (response: IPurchaseRequest[]) => {
        const result: IPurchaseRequest[] = [];
        response.forEach((item) => {
          if (isNaN(Number(item.Account))) {
            item.Account = "";
          }
          result.push(item);
        });
        return result;
      },
      */
      // todo Хак, сделан по просьбе бизнеса.
      transformResponse: (response: IPurchaseRequest[]) => {
        const result: IPurchaseRequest[] = [];
        response.forEach((item) => {
          if (containsRussianLetters(item.Account)) {
            item.Account = "";
          }
          result.push(item);
        });
        return result;
      },
      // Это тэг для автоматической отправки запроса с помощью встроенного механизма тэгов
      // RTK Query
      providesTags: ["PurchaseRequests"],
    }),

    /**
     * Запустить процесс формирования документов
     */
    createDocuments: build.mutation<
      ICommonResponse<ICreateDocumentsResponse>,
      IOrderDocumentsRequest
    >({
      query: (body) => ({
        url: getUrlFromPA(`v2/mf/create-documents`),
        method: "POST",
        body,
      }),
    }),

    /**
     * Запустить процесс подписания документов
     */
    signDocuments: build.mutation<
      ICommonResponse<ISignDocumentsResponse>,
      ISignDocumentsRequest
    >({
      query: (body) => ({
        url: getUrlFromPA(`v2/mf/documents-sign-notification`),
        method: "POST",
        body,
      }),
    }),

    /**
     * Получить документы, необходимые для оформления заявки (проверить готовы или нет документы)
     */
    getOrderDocuments: build.query<
      ICommonResponse<IOrderDocumentsWrapper>,
      {
        crmDocPackId: string | undefined;
      }
    >({
      query: ({ crmDocPackId }) => ({
        url: getUrlFromPA(
          `v2/mf/documents-created?crmDocPackId=${crmDocPackId}`
        ),
        method: "GET",
      }),
    }),

    /**
     * Сводный отчет по фондам у клиента
     */
    clientsFundsConsolidatedMF: build.query<
      IClientsFundsConsolidated[],
      {
        onDate: string;
      }
    >({
      query: ({ onDate }) => ({
        url: getUrlFromPA(`PA/MF/GetClientsFundsConsolidated/${onDate}`),
        method: "GET",
      }),
    }),
  }),
});
